/**
 * Authentication Context for React (Identity Platform)
 *
 * Provides authentication state and methods to all components.
 * Uses Firebase SDK with Google Cloud Identity Platform.
 * Supports: Email/Password (with registration & password reset), Google Sign-In
 */

import { useEffect, useState, useCallback, ReactNode } from 'react';
import {
  onAuthStateChange,
  signInWithGoogle,
  signInWithEmail,
  createAccount,
  changePassword as firebaseChangePassword,
  resetPassword,
  signOut as firebaseSignOut,
  getIdToken,
  getAuthErrorMessage,
  User,
} from '../firebase';
import { AuthContext, type AuthUser } from './auth';

/**
 * Convert Firebase User to AuthUser (role is fetched separately)
 */
function toAuthUser(firebaseUser: User, role: string | null = null): AuthUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    provider: firebaseUser.providerData[0]?.providerId || 'unknown',
    role,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        // Set user immediately (without role) so the UI doesn't flash login
        setUser(toAuthUser(firebaseUser));
        setError(null);

        // Fetch role from backend
        try {
          const token = await firebaseUser.getIdToken();
          const res = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const role = data.user?.role ?? null;
            setUser(toAuthUser(firebaseUser, role));
            setAccessDenied(false);
            setMustChangePassword(!!data.user?.must_change_password);
          }
        } catch {
          // Role fetch failed — user stays with role=null
        }
      } else {
        setUser(null);
        setAccessDenied(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const handleSignInWithGoogle = async () => {
    try {
      clearMessages();
      setLoading(true);
      await signInWithGoogle();
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    try {
      clearMessages();
      setLoading(true);
      await signInWithEmail(email, password);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (email: string, password: string, displayName?: string) => {
    try {
      clearMessages();
      setLoading(true);
      await createAccount(email, password, displayName);
      setSuccessMessage('Account created successfully!');
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (newPassword: string) => {
    try {
      clearMessages();
      setLoading(true);
      await firebaseChangePassword(newPassword);
      // Tell backend to clear the flag
      const token = await getIdToken();
      if (token) {
        await fetch('/api/auth/password-changed', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setMustChangePassword(false);
      setSuccessMessage('Password changed successfully!');
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      clearMessages();
      setLoading(true);
      await resetPassword(email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      clearMessages();
      await firebaseSignOut();
      setUser(null);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(authError.message || 'Failed to sign out');
      throw err;
    }
  };

  /**
   * Get authorization headers for API requests
   */
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getIdToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  const value = {
    user,
    loading,
    error,
    successMessage,
    accessDenied,
    mustChangePassword,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    createAccount: handleCreateAccount,
    changePassword: handleChangePassword,
    resetPassword: handleResetPassword,
    signOut: handleSignOut,
    clearMessages,
    getAuthHeaders,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context — re-exported for backwards compatibility.
 * Prefer importing from '../hooks/useAuth' directly.
 */
export { useAuth } from '../hooks/useAuth';
